import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Menu as MenuIcon, RefreshCw, Share2, Settings as SettingsIcon, Volume2, VolumeX, UserPlus, Plus, X as CloseIcon, Home as HomeIcon, FastForward } from 'lucide-react';

import { useGame } from '../../context/GameContext';
import { websocketService } from '../../services/websocketService';

/**
 * In-game bottom bar — replaces the global BottomNav while the user is
 * inside a room (lobby or active game) or viewing the post-game results.
 *
 * Per user spec: a menu on the bottom-left with start-new / rematch /
 * add-friend / share / settings / sounds. Plus a persistent context strip
 * (room code + status) so the user knows where they are at a glance.
 *
 * The drawer is the "Menu" surface — tap the menu button to open it.
 * Lightweight: no extra deps, opens an overlay with bottom-anchored
 * action list. Closing via X / backdrop / escape.
 */
export function InGameNav() {
  const navigate = useNavigate();
  const { state } = useGame();
  const [open, setOpen] = useState(false);
  const [muted, setMuted] = useState<boolean>(() => {
    try { return localStorage.getItem('wr.sound') === 'off'; } catch { return false; }
  });

  // Reflect the localStorage flag on mount + when settings page toggles.
  useEffect(() => {
    const onStorage = () => {
      try { setMuted(localStorage.getItem('wr.sound') === 'off'); } catch {}
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  // The .screen-pad CSS reserves padding-bottom = var(--bottom-nav-height)
  // when `.wr-with-bnav` is on <html>. BottomNav owns that class while
  // it's visible; in game routes BottomNav is hidden, so we set it here
  // instead — without this, the in-game content slipped behind the nav.
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const root = document.documentElement;
    root.classList.add('wr-with-bnav');
    return () => { root.classList.remove('wr-with-bnav'); };
  }, []);

  const roomCode = state.roomCode || '';
  const inLobby = state.currentScreen === 'lobby';
  const inResults = state.currentScreen === 'results';
  // Async-only host action: force-end the current round when a player has
  // gone idle. Surface only when game is async + I'm the host + we're
  // actually playing (not in lobby/results).
  const isAsync = !!state.gameSettings?.isAsync;
  const canForceEnd = isAsync && state.isHost && state.currentScreen === 'game';

  const toggleMute = () => {
    const next = !muted;
    setMuted(next);
    try { localStorage.setItem('wr.sound', next ? 'off' : 'on'); } catch {}
    import('../../services/soundService').then(m => {
      try { m.soundService.setEnabled(!next); } catch {}
    }).catch(() => {});
  };

  const handleShare = async () => {
    if (!roomCode) return;
    const url = `${window.location.origin}/join/${roomCode}`;
    const text = `Join my WordRush game: ${url}`;
    setOpen(false);
    if ((navigator as any).share) {
      try { await (navigator as any).share({ text, title: 'WordRush', url }); } catch {}
    } else {
      try {
        await navigator.clipboard.writeText(url);
        alert('Room link copied to clipboard.');
      } catch {}
    }
  };

  return (
    <>
      <nav
        className="bnav"
        role="navigation"
        aria-label="Game"
        style={{
          position: 'fixed',
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 50,
        }}
      >
        <button className="item" onClick={() => setOpen(true)} type="button" aria-label="Game menu">
          <MenuIcon size={20} strokeWidth={2.2} />
          <span>Menu</span>
        </button>

        {/* Context strip — middle of the bar shows where you are.
            Static (no click) so the surface stays calm during gameplay. */}
        <div
          className="item"
          style={{
            flex: 2,
            cursor: 'default',
            color: 'var(--ink-2)',
          }}
        >
          <span style={{ fontFamily: 'var(--mono)', fontSize: 11, fontWeight: 700, letterSpacing: '0.06em' }}>
            {roomCode || '—'}
          </span>
          <span style={{ fontSize: 10, color: 'var(--ink-3)' }}>
            {inResults
              ? 'Game over'
              : inLobby
                ? 'In lobby'
                : state.gameSettings?.isLadder
                  // A run has no denominator. See GameSettings.isLadder.
                  ? `Round ${state.currentRound || 1}`
                  : `Round ${state.currentRound || 1}/${state.totalRounds || 5}`}
          </span>
        </div>

        <button
          className="item"
          onClick={toggleMute}
          type="button"
          aria-label={muted ? 'Unmute sounds' : 'Mute sounds'}
        >
          {muted ? <VolumeX size={20} strokeWidth={2.2} /> : <Volume2 size={20} strokeWidth={2.2} />}
          <span>{muted ? 'Muted' : 'Sound'}</span>
        </button>

        <button className="item" onClick={handleShare} type="button" aria-label="Share game">
          <Share2 size={20} strokeWidth={2.2} />
          <span>Share</span>
        </button>
      </nav>

      {/* Action drawer — opens when Menu is tapped. The actions surface
          the things players actually want during/after a game without
          leaking into the calm in-game UI. */}
      {open && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Game menu"
          onClick={() => setOpen(false)}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 200,
            background: 'rgba(0,0,0,0.5)',
            backdropFilter: 'blur(6px)',
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'center',
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            className="card lg"
            style={{
              width: 'calc(100% - 32px)',
              maxWidth: 460,
              margin: 16,
              background: 'var(--surface)',
              padding: 20,
              maxHeight: '85vh',
              overflowY: 'auto',
            }}
          >
            <div className="wr-row" style={{ alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
              <span className="eyebrow">Game menu</span>
              <button
                onClick={() => setOpen(false)}
                aria-label="Close menu"
                type="button"
                style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--ink-3)' }}
              >
                <CloseIcon size={20} />
              </button>
            </div>

            <div className="wr-col gap-1">
              {/* Start a new game — only shown when no active game in
                  view. While in lobby/game, "Start new" lives elsewhere
                  (you'd leave first). */}
              {inResults && (
                <button
                  onClick={() => { setOpen(false); navigate(`/room/${roomCode}`); }}
                  type="button"
                  className="prow"
                  style={{ cursor: 'pointer', padding: '12px 14px' }}
                >
                  <RefreshCw size={20} color="var(--accent)" strokeWidth={2.2} />
                  <div className="body">
                    <div className="name" style={{ fontSize: 15 }}>Rematch</div>
                    <div className="word" style={{ fontSize: 12 }}>Play again with the same group</div>
                  </div>
                </button>
              )}

              {/* Force-end async round — host only, when the game's been
                  stuck waiting for someone. Submits blank for any
                  unsubmitted player and advances. */}
              {canForceEnd && (
                <button
                  onClick={() => {
                    setOpen(false);
                    if (!confirm("Force-end this round? Players who haven't submitted will score 0 for this round.")) return;
                    try {
                      const svc: any = websocketService;
                      if (svc.socket?.emit) svc.socket.emit('forceEndRound');
                    } catch (e) { console.warn('forceEndRound emit failed', e); }
                  }}
                  type="button"
                  className="prow"
                  style={{ cursor: 'pointer', padding: '12px 14px' }}
                >
                  <FastForward size={20} color="var(--accent)" strokeWidth={2.2} />
                  <div className="body">
                    <div className="name" style={{ fontSize: 15 }}>Force-end round</div>
                    <div className="word" style={{ fontSize: 12 }}>Move on if a player's gone idle</div>
                  </div>
                </button>
              )}

              <button
                onClick={() => { setOpen(false); navigate('/home'); }}
                type="button"
                className="prow"
                style={{ cursor: 'pointer', padding: '12px 14px' }}
              >
                <Plus size={20} color="var(--ink-2)" strokeWidth={2.2} />
                <div className="body">
                  <div className="name" style={{ fontSize: 15 }}>Start a new game</div>
                  <div className="word" style={{ fontSize: 12 }}>Pick a mode from Home</div>
                </div>
              </button>

              <button
                onClick={() => { setOpen(false); navigate('/friends'); }}
                type="button"
                className="prow"
                style={{ cursor: 'pointer', padding: '12px 14px' }}
              >
                <UserPlus size={20} color="var(--ink-2)" strokeWidth={2.2} />
                <div className="body">
                  <div className="name" style={{ fontSize: 15 }}>Add a friend</div>
                  <div className="word" style={{ fontSize: 12 }}>Connect with players in this game</div>
                </div>
              </button>

              <button
                onClick={() => { setOpen(false); handleShare(); }}
                type="button"
                className="prow"
                style={{ cursor: 'pointer', padding: '12px 14px' }}
              >
                <Share2 size={20} color="var(--ink-2)" strokeWidth={2.2} />
                <div className="body">
                  <div className="name" style={{ fontSize: 15 }}>Share game link</div>
                  <div className="word" style={{ fontSize: 12 }}>Invite spectators or other players</div>
                </div>
              </button>

              <button
                onClick={() => { setOpen(false); navigate('/settings'); }}
                type="button"
                className="prow"
                style={{ cursor: 'pointer', padding: '12px 14px' }}
              >
                <SettingsIcon size={20} color="var(--ink-2)" strokeWidth={2.2} />
                <div className="body">
                  <div className="name" style={{ fontSize: 15 }}>Settings</div>
                  <div className="word" style={{ fontSize: 12 }}>Sounds, theme, motion</div>
                </div>
              </button>

              <button
                onClick={() => { setOpen(false); navigate('/home'); }}
                type="button"
                className="prow"
                style={{ cursor: 'pointer', padding: '12px 14px', marginTop: 8 }}
              >
                <HomeIcon size={20} color="var(--ink-2)" strokeWidth={2.2} />
                <div className="body">
                  <div className="name" style={{ fontSize: 15 }}>Back to Home</div>
                </div>
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
